import { c as _c } from "react/compiler-runtime";
import jsonToTS from 'json-to-ts';
import { createContext, useContext, useState } from 'react';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import { EventAttemptResponseBody } from '../../../../../../../typings/EventAttemptResponse.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { capitalizeFirstLetter } from '../../../../utils';
import { copyTextToClipboard } from '../../../../utils/copy';
import jsonToGo from '../../../../utils/type-extract/go';
import Button from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Loading from '../../../common/base/Loading';
import Text from '../../../common/base/Text';
import DropdownMenu from '../../../common/DropdownMenu';
import Editor, { EditorLanguage } from '../../../common/Editor';
import { Div, StyledSpacingProp } from '../../../common/helpers/StyledUtils';
import JSONBrowser, { ContextMenuConfig } from '../../../common/JSONBrowser';
import Search from '../../../common/Search';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
type Props = {
  text: string | object;
  raw?: string | object;
  raw_as_text?: boolean;
  display_type?: DisplayOptionType;
  on_display_type_change?: (type: DisplayOptionType) => void;
  lang?: EditorLanguage;
  embedded?: boolean;
  filters_width?: number;
  filters_margin?: StyledSpacingProp;
  custom_search_fn?: (text: string | object, search_value: string) => string | object;
  // Props for raw body fetching (similar to RequestBody)
  type?: 'event' | 'request' | 'bookmark';
  id?: string;
  context_menu?: boolean;
  context_menu_config?: ContextMenuConfig | null;
};
const display_options = [{
  label: 'Pretty',
  key: 'pretty'
}, {
  label: 'Raw',
  key: 'raw'
}, {
  label: 'Go',
  key: 'golang'
}, {
  label: 'TypeScript',
  key: 'typescript'
}];
export type DisplayOptionType = 'pretty' | 'raw' | 'golang' | 'typescript' | 'lang';

// Create the CodeBlock context
const CodeBlockContext = createContext<{
  text: string | object;
  lang?: EditorLanguage;
  embedded?: boolean;
  filters_width?: number;
  filters_margin?: StyledSpacingProp;
  search_value: string;
  setSearchValue: (value: string) => void;
  display_type: DisplayOptionType;
  setBodyDisplayType: (type: DisplayOptionType) => void;
  body_type: DisplayOptionType;
  data_to_display: EventAttemptResponseBody;
  is_json: boolean;
  theme: any;
  addToast: (appearance: string, message: string) => void;
  fetch_remote_raw_body: boolean;
  raw_body_response?: any;
  context_menu?: boolean;
  context_menu_config?: ContextMenuConfig | null;
}>({
  text: '',
  search_value: '',
  setSearchValue: () => {
    /* Default empty implementation */
  },
  display_type: 'pretty',
  setBodyDisplayType: () => {
    /* Default empty implementation */
  },
  body_type: 'pretty',
  data_to_display: '',
  is_json: false,
  theme: {},
  addToast: () => {
    /* Default empty implementation */
  },
  fetch_remote_raw_body: false,
  raw_body_response: undefined,
  context_menu: false,
  context_menu_config: null
});
const useRawBody = t0 => {
  const $ = _c(15);
  const {
    resolved_body_type,
    type,
    id,
    raw_as_text
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const fetch_remote_raw_body = Boolean(resolved_body_type === "raw" && type && id);
  let t1;
  if ($[0] !== fetch_remote_raw_body || $[1] !== id || $[2] !== type) {
    t1 = fetch_remote_raw_body && APIMethodKeys[`${type}s`].getRawBody(id);
    $[0] = fetch_remote_raw_body;
    $[1] = id;
    $[2] = type;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const raw_body_key = t1;
  let t2;
  if ($[4] !== raw_as_text || $[5] !== raw_body_key) {
    t2 = raw_body_key && (raw_as_text ? [raw_body_key, "text"] : raw_body_key);
    $[4] = raw_as_text;
    $[5] = raw_body_key;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== HookdeckAPI || $[8] !== id || $[9] !== raw_as_text || $[10] !== type) {
    t3 = async () => {
      const response = await HookdeckAPI[`${type}s`].getRawBody(id);
      return raw_as_text && response instanceof Blob ? {
        body: await response.text()
      } : response;
    };
    $[7] = HookdeckAPI;
    $[8] = id;
    $[9] = raw_as_text;
    $[10] = type;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data: raw_body_response
  } = useSWR(t2, t3);
  let t4;
  if ($[12] !== fetch_remote_raw_body || $[13] !== raw_body_response) {
    t4 = {
      fetch_remote_raw_body,
      raw_body_response
    };
    $[12] = fetch_remote_raw_body;
    $[13] = raw_body_response;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};

// Create reusable components
const RawBody: React.FC<{
  loading: boolean;
  body: EventAttemptResponseBody;
  compact?: boolean;
}> = t0 => {
  const $ = _c(5);
  const {
    loading,
    body
  } = t0;
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loading />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (body === undefined || body === "" || body === null) {
    const t1 = body === "" ? "Empty" : String(body);
    let t2;
    if ($[1] !== t1) {
      t2 = <Text muted={true} capitalize={true} mono={true} size="s">{t1}</Text>;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const t1 = String(body);
  let t2;
  if ($[3] !== t1) {
    t2 = <Text as="span" white_space="pre-wrap" word_break="break-all" mono={true} size="s">{t1}</Text>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const TypesBody: React.FC<{
  types: string | null;
  compact: boolean;
}> = t0 => {
  const $ = _c(3);
  const {
    types,
    compact
  } = t0;
  let t1;
  if ($[0] !== compact || $[1] !== types) {
    t1 = types ? <Editor language="typescript" value={types} height={`${types.split("\n").length * (compact ? 18 : 20) + 20}px`} small_text={compact} disabled={true} scroll={false} /> : <Div flex={{
      justify: "center"
    }}><Loading /></Div>;
    $[0] = compact;
    $[1] = types;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const LangEditor: React.FC<{
  lang: EditorLanguage;
  content: string;
}> = t0 => {
  const $ = _c(3);
  const {
    lang,
    content
  } = t0;
  let t1;
  if ($[0] !== content || $[1] !== lang) {
    t1 = lang && content ? <Editor language={lang} value={content} height={`${content.split("\n").length * 20 + 20}px`} disabled={true} scroll={false} /> : <Div flex={{
      justify: "center"
    }}><Loading /></Div>;
    $[0] = content;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

// CodeBlock Provider component
const CodeBlockProvider: React.FC<Props & {
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(44);
  const {
    text,
    raw,
    raw_as_text,
    display_type: display_type_prop,
    on_display_type_change,
    lang,
    embedded,
    filters_width,
    filters_margin,
    custom_search_fn,
    type,
    id,
    context_menu: t1,
    context_menu_config: t2,
    children
  } = t0;
  const context_menu = t1 === undefined ? false : t1;
  const context_menu_config = t2 === undefined ? null : t2;
  const {
    addToast
  } = useToasts();
  const [search_value, setSearchValue] = useState("");
  const theme = useTheme();
  const [display_type_preference, setDisplayTypePreference] = useLocalStorage("pref:code-block-display-type", "pretty");
  const display_type = display_type_prop || display_type_preference;
  const setBodyDisplayType = on_display_type_change ?? setDisplayTypePreference;
  const is_json = Boolean(text && typeof text === "object");
  const body_type = lang ? "lang" : display_type || "pretty";
  let t3;
  if ($[0] !== body_type || $[1] !== id || $[2] !== raw_as_text || $[3] !== type) {
    t3 = {
      resolved_body_type: body_type,
      type,
      id,
      raw_as_text
    };
    $[0] = body_type;
    $[1] = id;
    $[2] = raw_as_text;
    $[3] = type;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    fetch_remote_raw_body,
    raw_body_response
  } = useRawBody(t3);
  let data_to_display = text;
  if (custom_search_fn && search_value) {
    let t4;
    if ($[5] !== custom_search_fn || $[6] !== search_value || $[7] !== text) {
      t4 = custom_search_fn(text, search_value);
      $[5] = custom_search_fn;
      $[6] = search_value;
      $[7] = text;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    data_to_display = t4;
  } else {
    if (body_type === "pretty") {
      data_to_display = text;
    } else {
      if (body_type === "raw") {
        let t4;
        if ($[9] !== fetch_remote_raw_body || $[10] !== raw || $[11] !== raw_body_response || $[12] !== text) {
          t4 = fetch_remote_raw_body ? raw_body_response instanceof Blob ? null : raw_body_response?.body : raw || (typeof text === "string" ? text : JSON.stringify(text));
          $[9] = fetch_remote_raw_body;
          $[10] = raw;
          $[11] = raw_body_response;
          $[12] = text;
          $[13] = t4;
        } else {
          t4 = $[13];
        }
        data_to_display = t4;
      } else {
        if (body_type === "typescript") {
          let t4;
          if ($[14] !== is_json || $[15] !== search_value || $[16] !== text) {
            t4 = is_json ? jsonToTS(text, {
              rootName: "Body"
            }).filter(ts_interface => !search_value || ts_interface.includes(search_value)).join("\n") : "";
            $[14] = is_json;
            $[15] = search_value;
            $[16] = text;
            $[17] = t4;
          } else {
            t4 = $[17];
          }
          data_to_display = t4;
        } else {
          if (body_type === "golang") {
            let t4;
            if ($[18] !== search_value || $[19] !== text) {
              let t5;
              if ($[21] !== search_value) {
                t5 = line => !search_value || line.includes(search_value);
                $[21] = search_value;
                $[22] = t5;
              } else {
                t5 = $[22];
              }
              t4 = jsonToGo(JSON.stringify(text), "Body", true).go.split("\n").filter(t5).join("\n");
              $[18] = search_value;
              $[19] = text;
              $[20] = t4;
            } else {
              t4 = $[20];
            }
            data_to_display = t4;
          }
        }
      }
    }
  }
  let t4;
  if ($[23] !== addToast || $[24] !== body_type || $[25] !== context_menu || $[26] !== context_menu_config || $[27] !== data_to_display || $[28] !== display_type || $[29] !== embedded || $[30] !== fetch_remote_raw_body || $[31] !== filters_margin || $[32] !== filters_width || $[33] !== is_json || $[34] !== lang || $[35] !== raw_body_response || $[36] !== search_value || $[37] !== setBodyDisplayType || $[38] !== text || $[39] !== theme) {
    t4 = {
      text,
      lang,
      embedded,
      filters_width,
      filters_margin,
      search_value,
      setSearchValue,
      display_type,
      setBodyDisplayType,
      body_type,
      data_to_display,
      is_json,
      theme,
      addToast,
      fetch_remote_raw_body,
      raw_body_response,
      context_menu,
      context_menu_config
    };
    $[23] = addToast;
    $[24] = body_type;
    $[25] = context_menu;
    $[26] = context_menu_config;
    $[27] = data_to_display;
    $[28] = display_type;
    $[29] = embedded;
    $[30] = fetch_remote_raw_body;
    $[31] = filters_margin;
    $[32] = filters_width;
    $[33] = is_json;
    $[34] = lang;
    $[35] = raw_body_response;
    $[36] = search_value;
    $[37] = setBodyDisplayType;
    $[38] = text;
    $[39] = theme;
    $[40] = t4;
  } else {
    t4 = $[40];
  }
  const contextValue = t4;
  let t5;
  if ($[41] !== children || $[42] !== contextValue) {
    t5 = <CodeBlockContext.Provider value={contextValue}>{children}</CodeBlockContext.Provider>;
    $[41] = children;
    $[42] = contextValue;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  return t5;
};

// Subcomponents that use the context
const SearchComponent: React.FC<{
  m?: StyledSpacingProp;
}> = t0 => {
  const $ = _c(7);
  const {
    m
  } = t0;
  const {
    search_value,
    setSearchValue,
    filters_width,
    display_type
  } = useContext(CodeBlockContext);
  if (display_type !== "pretty") {
    return null;
  }
  let t1;
  if ($[0] !== filters_width) {
    t1 = filters_width || {
      px: 468
    };
    $[0] = filters_width;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== m || $[3] !== search_value || $[4] !== setSearchValue || $[5] !== t1) {
    t2 = <Search w={t1} m={m} small={true} value={search_value} onChange={setSearchValue} placeholder="Filter" />;
    $[2] = m;
    $[3] = search_value;
    $[4] = setSearchValue;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const DropdownDisplayType: React.FC<{
  ghost?: boolean;
}> = t0 => {
  const $ = _c(11);
  const {
    ghost
  } = t0;
  const {
    lang,
    body_type,
    setBodyDisplayType
  } = useContext(CodeBlockContext);
  if (lang) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      px: 164
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== body_type) {
    t2 = capitalizeFirstLetter(body_type);
    $[1] = body_type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !ghost;
  let t4;
  if ($[3] !== body_type || $[4] !== setBodyDisplayType) {
    t4 = display_options.map(option => ({
      ...option,
      selected: option.key === body_type,
      onClick: () => setBodyDisplayType(option.key as DisplayOptionType)
    }));
    $[3] = body_type;
    $[4] = setBodyDisplayType;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== ghost || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <DropdownMenu w={t1} label={t2} outline={t3} ghost={ghost} placement="bottom-end" expand_icon="chevron_down" options={t4} />;
    $[6] = ghost;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
const ButtonCopy: React.FC<{
  ghost?: boolean;
  value?: string;
}> = t0 => {
  const $ = _c(11);
  const {
    ghost,
    value
  } = t0;
  const {
    data_to_display,
    display_type,
    addToast
  } = useContext(CodeBlockContext);
  let copy_value;
  if ($[0] !== data_to_display || $[1] !== display_type || $[2] !== value) {
    copy_value = value || String(data_to_display);
    if (display_type === "pretty" && typeof data_to_display === "object") {
      copy_value = JSON.stringify(data_to_display, null, 2);
    }
    $[0] = data_to_display;
    $[1] = display_type;
    $[2] = value;
    $[3] = copy_value;
  } else {
    copy_value = $[3];
  }
  const t1 = !ghost;
  let t2;
  if ($[4] !== addToast || $[5] !== copy_value) {
    t2 = () => {
      copyTextToClipboard(copy_value);
      addToast("success", "Copied to clipboard");
    };
    $[4] = addToast;
    $[5] = copy_value;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== ghost || $[8] !== t1 || $[9] !== t2) {
    t3 = <Button outline={t1} ghost={ghost} icon="copy" onClick={t2} />;
    $[7] = ghost;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
const CodeContent: React.FC = () => {
  const $ = _c(32);
  const {
    lang,
    embedded,
    body_type,
    is_json,
    data_to_display,
    search_value,
    fetch_remote_raw_body,
    raw_body_response,
    context_menu,
    context_menu_config
  } = useContext(CodeBlockContext);
  let t0;
  if ($[0] !== body_type || $[1] !== embedded) {
    t0 = ["typescript", "golang"].includes(body_type) ? {
      t: embedded ? 0 : 2,
      x: 2,
      b: 4
    } : body_type === "lang" ? {
      x: 2,
      b: 2,
      t: embedded ? 0 : 2
    } : {
      x: 6,
      b: 6,
      t: embedded ? 0 : 6
    };
    $[0] = body_type;
    $[1] = embedded;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== body_type || $[4] !== data_to_display || $[5] !== lang) {
    t1 = body_type === "lang" && <LangEditor lang={lang} content={data_to_display as string} />;
    $[3] = body_type;
    $[4] = data_to_display;
    $[5] = lang;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== body_type || $[8] !== context_menu || $[9] !== context_menu_config || $[10] !== data_to_display || $[11] !== search_value) {
    t2 = body_type === "pretty" && <JSONBrowser search_term={search_value} json={data_to_display as any} context_menu={context_menu} context_menu_config={context_menu_config} />;
    $[7] = body_type;
    $[8] = context_menu;
    $[9] = context_menu_config;
    $[10] = data_to_display;
    $[11] = search_value;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== body_type || $[14] !== data_to_display || $[15] !== fetch_remote_raw_body || $[16] !== raw_body_response) {
    t3 = body_type === "raw" && <RawBody body={data_to_display} loading={!!fetch_remote_raw_body && !raw_body_response} />;
    $[13] = body_type;
    $[14] = data_to_display;
    $[15] = fetch_remote_raw_body;
    $[16] = raw_body_response;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== body_type || $[19] !== data_to_display || $[20] !== embedded || $[21] !== is_json) {
    t4 = ["typescript", "golang"].includes(body_type) && (is_json ? <TypesBody compact={false} types={String(data_to_display)} /> : <Div p={{
      x: 4,
      t: !embedded ? 4 : 0,
      b: !embedded ? 2 : 0
    }}><Text muted={true}>Not supported for non-json types</Text></Div>);
    $[18] = body_type;
    $[19] = data_to_display;
    $[20] = embedded;
    $[21] = is_json;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== t0 || $[24] !== t1 || $[25] !== t2 || $[26] !== t3 || $[27] !== t4) {
    t5 = <StyledCardSection muted={true} p={t0}>{t1}{t2}{t3}{t4}</StyledCardSection>;
    $[23] = t0;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== embedded || $[30] !== t5) {
    t6 = <StyledCard overflow_hidden={true} borderless={embedded}>{t5}</StyledCard>;
    $[29] = embedded;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  return t6;
};

// Create the main CodeBlock component and add subcomponents
const CodeBlock = Object.assign((props: Props) => {
  return <CodeBlockProvider {...props}>
        <Div flex={{
      direction: 'column',
      gap: props.embedded ? 0 : 4
    }}>
          <Div flex={{
        gap: 3
      }} m={props.filters_margin}>
            <CodeBlock.Search />
            {!props.display_type && <CodeBlock.DropdownDisplayType />}
            <CodeBlock.ButtonCopy />
          </Div>
          <CodeBlock.Code />
        </Div>
      </CodeBlockProvider>;
}, {
  Provider: CodeBlockProvider,
  Search: SearchComponent,
  DropdownDisplayType: DropdownDisplayType,
  ButtonCopy: ButtonCopy,
  Code: CodeContent
});
export { CodeBlock };